import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { PagingService } from '../../services/paging/paging.service';
import { DocumentData, QuerySnapshot } from '@angular/fire/compat/firestore';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { defer, EMPTY, from, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { PagingQueryOptions } from '../../models/pagingQueryOptions ';
import { isNullOrUndefined } from '@pineapplelab/util';

const NO_SEARCH_YET = Symbol('no-search-yet');

@Component({
  selector: 'app-paging',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ],
  providers: [PagingService],
  templateUrl: './paging.component.html',
  styleUrls: ['./paging.component.scss']
})
export class PagingComponent implements OnInit, OnChanges {

  @Input() options: PagingQueryOptions;
  @Input() scrollTopOnPageChange: boolean = false;
  /**
   * Sequential-only paging: renders just prev/next and never jumps to an arbitrary page.
   * Meant for very large collections, where both the total-count aggregation and the
   * "read every doc up to page N" jump query are prohibitively expensive.
   */
  @Input() sequentialPaging: boolean = false;

  @Output() itemsChange: EventEmitter<DocumentData[]> = new EventEmitter<DocumentData[]>();
  @Output() onPagingExecuted = new EventEmitter<void>();
  @Output() currentPageNumber = new EventEmitter<number>();
  /**
   * True while any page fetch is in flight, including a typed search waiting out its debounce.
   * Emitted from a microtask, so parents can bind it without tripping ExpressionChangedAfterItHasBeenChecked.
   */
  @Output() loadingChange = new EventEmitter<boolean>();

  items: DocumentData[] = [];
  currentPage: number = 1;
  totalPages: number[] = [];
  totalPagesCount: number = 0;
  hasNextPage: boolean = false;
  loading: boolean = false;
  loadingMorePages: boolean = false;
  private searchSubject: Subject<string> = new Subject();
  private activeLoads = 0;
  private searchPending = false;
  private lastEmittedLoading = false;
  private loadingEmitScheduled = false;
  /** Last debounced search value; mirrors what distinctUntilChanged compares against. */
  private lastSearchText: string | typeof NO_SEARCH_YET = NO_SEARCH_YET;

  constructor(private pagingService: PagingService) { }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.loadFirstPage(), this.getPageNumbers()]);

    this.searchSubject.pipe(
      debounceTime(300),
      tap(searchText => {
        // The debounce window is over; from here on an actual fetch (if any) is tracked by trackLoading.
        this.lastSearchText = searchText;
        this.searchPending = false;
        this.scheduleLoadingEmit();
      }),
      distinctUntilChanged(),
      switchMap(searchText => {
        if(!isNullOrUndefined(searchText)) {
          this.options.searchText = searchText;
          return defer(() => {
            this.beginLoad();
            return from(this.pagingService.getFirstPage(this.options, this.sequentialPaging));
          }).pipe(finalize(() => this.endLoad()));
        } else {
          return EMPTY;
        }
      })
    ).subscribe(snapshot => {
      this.currentPage = 1;
      this.updateItems(snapshot);
      this.getPageNumbers();
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['options'] && !changes['options'].firstChange) {
      if (!this.options.searchText) {
        this.currentPage = 1;
        this.emitCurrentPage();
      }
      this.trackSearch(this.options.searchText);
      this.searchSubject.next(this.options.searchText);
    }

    if (changes['options'] && (changes['options'].currentValue.orderBy || changes['options'].currentValue.orderDirection) && changes['options'].currentValue.refreshPage) {
      await this.loadFirstPage();
	  this.onPagingExecuted.emit();
    }
  }

  async loadFirstPage() {
    this.currentPage = 1;
    const snapshot = await this.trackLoading(() => this.pagingService.getFirstPage(this.options, this.sequentialPaging));
    this.updateItems(snapshot);
    this.getPageNumbers();
    this.emitCurrentPage();
  }

  async loadNextPage() {
    if (this.sequentialPaging && !this.hasNextPage) {
      return;
    }
    this.loading = true;
    this.currentPage++;
    if (this.options.lastVisible) {
      const snapshot = await this.trackLoading(() => this.pagingService.getNextPage(this.options, this.sequentialPaging));
      this.updateItems(snapshot);
      if (this.scrollTopOnPageChange) {
        this.scrollToTopOfList();
      }
    }
    this.emitCurrentPage();
    this.loading = false;
    if (!this.sequentialPaging && this.currentPage > this.totalPages.length - 2) {
      await this.getPageNumbers(this.totalPages.length + 10);
    }
  }

  async loadPrevPage() {
    if (this.currentPage <= 1) {
      return;
    }
    this.loading = true;
    this.currentPage--;
    if (this.options.firstVisible) {
      const snapshot = await this.trackLoading(() => this.pagingService.getPrevPage(this.options));
      this.updateItems(snapshot);
      // We came from the page after this one, so there is always a page forward.
      this.hasNextPage = true;
      if (this.scrollTopOnPageChange) {
        this.scrollToTopOfList();
      }
    }
    this.emitCurrentPage()
    this.loading = false;
  }

  async loadPage(pageNumber: number) {
    if (this.sequentialPaging) {
      return;
    }
    this.loading = true;
    this.options.pageNumber = pageNumber;
    const snapshot = await this.trackLoading(() => this.pagingService.getPage(this.options));
    this.currentPage = pageNumber;
    this.updateItems(snapshot);
    if (this.scrollTopOnPageChange) {
      this.scrollToTopOfList();
    }
    this.emitCurrentPage()
    this.loading = false;
    await this.getPageNumbers(this.currentPage > this.totalPages.length - 2 ? this.totalPages.length + 10 : this.totalPages.length);
  }

  private async trackLoading<T>(fetch: () => Promise<T>): Promise<T> {
    this.beginLoad();
    try {
      return await fetch();
    } finally {
      this.endLoad();
    }
  }

  private beginLoad(): void {
    this.activeLoads++;
    this.scheduleLoadingEmit();
  }

  private endLoad(): void {
    this.activeLoads = Math.max(0, this.activeLoads - 1);
    this.scheduleLoadingEmit();
  }

  /** Marks a typed search as pending from the keystroke, not only once the debounce fires. */
  private trackSearch(searchText: string): void {
    if (!isNullOrUndefined(searchText) && searchText !== this.lastSearchText) {
      this.searchPending = true;
      this.scheduleLoadingEmit();
    }
  }

  /** Coalesces begin/end pairs within one tick so listeners don't see a false→true flicker. */
  private scheduleLoadingEmit(): void {
    if (this.loadingEmitScheduled) {
      return;
    }
    this.loadingEmitScheduled = true;
    queueMicrotask(() => {
      this.loadingEmitScheduled = false;
      const isLoading = this.activeLoads > 0 || this.searchPending;
      if (isLoading !== this.lastEmittedLoading) {
        this.lastEmittedLoading = isLoading;
        this.loadingChange.emit(isLoading);
      }
    });
  }

  private updateItems(snapshot: QuerySnapshot<DocumentData>) {
    let docs = snapshot.docs;
    if (this.sequentialPaging) {
      // The query asked for one extra document: its presence is what tells us a next page exists.
      this.hasNextPage = docs.length > this.options.pageSize;
      docs = docs.slice(0, this.options.pageSize);
    }
    this.items = docs.map(doc => doc.data());
    this.options.lastVisible = docs[docs.length - 1];
    this.options.firstVisible = docs[0];
    this.itemsChange.emit(this.items);
  }

  async getPageNumbers(maxPages = 10): Promise<void> {
    if (this.sequentialPaging) {
      return;
    }
    this.loadingMorePages = true;
    const totalItems = await this.pagingService.getTotalDocumentsCount(this.options);
    this.totalPagesCount = Math.ceil(totalItems / this.options.pageSize);
    const currentlyVisibleCount = Math.min(this.totalPagesCount, maxPages);
    this.totalPages = Array.from({ length: currentlyVisibleCount }, (_, i) => i + 1);
    this.loadingMorePages = false;
  }
  
  scrollToTopOfList(): void {
    const element = document.querySelector('.content-container');
    if (element) {
      element.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  }

  scrollIfNecessary(selectedPage: string | number): void {
    const pagesContainer = document.querySelector('.pages');
    if (!pagesContainer) {
      return;
    }

    let selectedButton: Element;

    if ((+selectedPage - 1) === this.totalPagesCount) {
      selectedPage = +selectedPage - 1;
      selectedButton = pagesContainer.children[pagesContainer.children.length - 3]
    } else {
      selectedButton = pagesContainer.children.namedItem(`${selectedPage}`);
    }
    if (!selectedButton) {
      return;
    }
    if (selectedButton.getBoundingClientRect().right > pagesContainer.getBoundingClientRect().right) {
      pagesContainer.scrollLeft += selectedButton.getBoundingClientRect().right - pagesContainer.getBoundingClientRect().right;
      return;
    }
    if (selectedButton.getBoundingClientRect().left < pagesContainer.getBoundingClientRect().left) {
      pagesContainer.scrollLeft -= pagesContainer.getBoundingClientRect().left - selectedButton.getBoundingClientRect().left;
      return;
    }
    if (+selectedPage > this.currentPage) {
      pagesContainer.scrollLeft += 40;
    } else {
      pagesContainer.scrollLeft -= 40;
    }
  }

  getVisiblePages(): (number | string)[] {
    const blockSize = 9;
    if (this.totalPagesCount <= blockSize) {
      return Array.from({ length: this.totalPagesCount }, (_, i) => i + 1);
    }
    const startPage = 1;
    const endPage = this.totalPagesCount;
    let currentBlockStart = Math.max(this.currentPage - Math.floor(blockSize / 2), 2);
    let currentBlockEnd = Math.min(this.currentPage + Math.floor(blockSize / 2), endPage - 1);
    if (endPage - this.currentPage < Math.floor(blockSize / 2)) {
      currentBlockStart = Math.max(endPage - blockSize + 2, 2);
      currentBlockEnd = endPage - 1;
    }
    if (this.currentPage <= Math.ceil(blockSize / 2)) {
      currentBlockStart = 2;
      currentBlockEnd = Math.min(blockSize - 1, endPage - 1);
    }
    const pages: (number | string)[] = [startPage];
    if (currentBlockStart > startPage + 1) pages.push('...');
    pages.push(...Array.from({ length: currentBlockEnd - currentBlockStart + 1 }, (_, i) => currentBlockStart + i));
    if (currentBlockEnd < endPage - 1) pages.push('...');
    pages.push(endPage);
    return pages;
  }

  onPageClick(page: number | string): void {
    if (page === '...') {
      return;
    }
    if (typeof page === 'number') {
      this.loadPage(page);
    }
  }

  emitCurrentPage() {
    this.currentPageNumber.emit(this.currentPage);
  }

}
